import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';

export type SeoScan = {
  ran_at: string;
  source: 'on_demand' | 'cron';
  sitemap_status: 'valid' | 'missing' | 'invalid';
  sitemap_url_count: number;
  robots_status: 'optimized' | 'missing' | 'permissive';
  pages_total: number;
  pages_ok: number;
  pages_broken: number;
  broken_sample: { url: string; status: number }[];
  duration_ms: number;
  error?: string;
};

export type VitalsSummary = {
  since: string;
  metrics: Record<string, { median: number; count: number }>;
  traffic: { mobile_pct: number; desktop_pct: number; total_beacons: number };
};

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  // Attach the Supabase access token so the worker's `requireAdmin`
  // gate can verify the caller. Anonymous → no header → worker 401s.
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { ...init, headers });
  const text = await res.text();

  // Vite's dev server doesn't serve Cloudflare Pages Functions — if you
  // hit /api/seo/* under `npm run dev` you'll get back the SPA HTML, an
  // empty body, or a 404. Surface that as a clear, actionable error
  // instead of letting JSON.parse throw a cryptic "unexpected end of data".
  if (!text) {
    throw new Error(
      `Empty response from ${url} (HTTP ${res.status}). If you're on ` +
        `localhost, you must browse via wrangler on http://localhost:8788 ` +
        `(not vite's :5173). Vite doesn't serve Pages Functions.`,
    );
  }
  let body: { ok: boolean; data?: T; error?: string };
  try {
    body = JSON.parse(text);
  } catch {
    throw new Error(
      `Non-JSON response from ${url} (HTTP ${res.status}). The endpoint ` +
        `may not be deployed. First 80 chars: ${text.slice(0, 80)}`,
    );
  }
  if (!body.ok) throw new Error(body.error ?? `request failed: ${res.status}`);
  return body.data as T;
}

export function useLatestScan() {
  return useQuery({
    queryKey: ['seo', 'scan', 'latest'],
    queryFn: () => fetchJson<SeoScan | null>('/api/seo/scan'),
    staleTime: 60_000,
  });
}

export function useVitalsSummary() {
  return useQuery({
    queryKey: ['seo', 'vitals', 'summary'],
    queryFn: () => fetchJson<VitalsSummary>('/api/seo/vitals'),
    staleTime: 60_000,
  });
}

export function useRunScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchJson<SeoScan>('/api/seo/scan', { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(['seo', 'scan', 'latest'], data);
    },
  });
}
